<script setup>
import { computed, ref } from 'vue';
import draggable from 'vuedraggable';
import ServiceOrderCard from './ServiceOrderCard.vue';
import { getColumnColorClasses } from '../utils/columnColors';

const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  pipeline: {
    type: String,
    default: 'service_orders',
  },
  crmCurrency: {
    type: String,
    default: 'USD',
  },
  canDeleteOrder: {
    type: Boolean,
    default: false,
  },
  columnWidthStyle: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'orderMoved',
  'orderEdit',
  'orderDelete',
  'orderClick',
  'dragStarted',
  'dragEnded',
]);

const colorClasses = computed(() => {
  return getColumnColorClasses(props.column.color);
});

const orderCount = computed(() => {
  return props.column.service_orders?.length || 0;
});

const activeDragOrderId = ref(null);

const onOrderClick = order => {
  emit('orderClick', order);
};

const onOrderEdit = order => {
  emit('orderEdit', order);
};

const onOrderDelete = order => {
  emit('orderDelete', order);
};

const onDragStart = event => {
  activeDragOrderId.value = Number(event?.item?.dataset?.orderId) || null;
  emit('dragStarted');
};

const onDragEnd = () => {
  activeDragOrderId.value = null;
  emit('dragEnded');
};

const onDragChange = event => {
  if (!event.added && !event.moved) {
    return;
  }

  const movedItem = event.added ? event.added.element : event.moved.element;
  const movedIndex = event.added ? event.added.newIndex : event.moved.newIndex;

  emit('orderMoved', {
    orderId: movedItem.id,
    kanbanColumnId: props.column.id,
    position: movedIndex + 1,
  });
};
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-shrink-0 flex-col rounded-xl border border-n-weak bg-n-alpha-1 p-3 dark:bg-n-solid-2"
    :style="columnWidthStyle"
  >
    <div
      class="sticky top-0 z-10 mb-3 flex items-center justify-between bg-n-alpha-1 px-1 py-1 dark:bg-n-solid-2"
    >
      <h3
        class="font-semibold text-sm text-n-slate-12 border-b-2 pb-1"
        :class="[colorClasses.border]"
      >
        {{ column.name }}
      </h3>
      <span
        class="text-xs font-medium bg-n-alpha-2 text-n-slate-11 py-0.5 px-2 rounded-full inline-flex items-center gap-1"
      >
        <span class="w-1.5 h-1.5 rounded-full" :class="colorClasses.dot" />
        {{ orderCount }}
      </span>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto pr-1">
      <draggable
        class="space-y-2 pb-1"
        :list="column.service_orders"
        group="service-orders"
        item-key="id"
        :animation="180"
        force-fallback
        fallback-on-body
        :swap-threshold="0.65"
        ghost-class="opacity-35"
        chosen-class="shadow-xl"
        drag-class="cursor-grabbing"
        @start="onDragStart"
        @end="onDragEnd"
        @change="onDragChange"
      >
        <template #item="{ element }">
          <div
            :data-order-id="element.id"
            class="w-full transition-transform duration-200 ease-out"
            :class="
              activeDragOrderId === element.id
                ? 'scale-[1.02] -rotate-1'
                : 'scale-100 rotate-0'
            "
          >
            <ServiceOrderCard
              :service-order="element"
              :pipeline="pipeline"
              :currency="crmCurrency"
              :can-delete="canDeleteOrder"
              :is-dragging="activeDragOrderId === element.id"
              @click="onOrderClick"
              @edit="onOrderEdit"
              @delete="onOrderDelete"
            />
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>
